import React, { useEffect, useMemo, useState } from "react";
import SharedLayout from "./SharedLayout";
import { FiChevronDown } from "react-icons/fi";
import axios from "axios";
import { useParams } from "react-router-dom";
import InfoModal from "./modals/InfoModals";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ProgressCircle = ({ value = 0, size = 72, stroke = 6, label, sub }) => {
  const pct = Math.max(0, Math.min(100, Number(value) || 0));
  const r = size / 2 - stroke / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (pct / 100) * c;

  return (
    <div className="flex flex-col items-center">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="#e6e6e6"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="#07183c"
          strokeWidth={stroke}
          strokeDasharray={c}
          strokeDashoffset={offset}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: "stroke-dashoffset .5s ease" }}
        />
        <text
          x="50%"
          y="50%"
          dy=".3em"
          textAnchor="middle"
          fontWeight="700"
          fontSize={size / 5}
        >
          %{pct}
        </text>
      </svg>
      {label && <span className="mt-2 text-xs sm:text-sm">{label}</span>}
      {sub && <span className="text-[10px] text-gray-500 mt-0.5">{sub}</span>}
    </div>
  );
};

const Select = ({ children, value, onChange }) => (
  <div className="relative w-full sm:w-64">
    <select
      value={value}
      onChange={onChange}
      className="appearance-none w-full pl-4 pr-10 py-2 rounded-full border border-gray-300
                 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500"
    >
      {children}
    </select>
    <FiChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-500" />
  </div>
);

// JSON → Excel helper
const exportToExcel = (data, fileName) => {
  const worksheet = XLSX.utils.json_to_sheet(data);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
  const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
  saveAs(blob, fileName + ".xlsx");
};

const InsightsPage = () => {
  const { userId } = useParams();

  // state
  const [courses, setCourses] = useState([]);
  const [selectedCourseId, setSelectedCourseId] = useState("");
  const [courseLessons, setCourseLessons] = useState([]);
  const [selectedLessonIdx, setSelectedLessonIdx] = useState("");
  const [learnerInsights, setLearnerInsights] = useState([]);

  // modals
  const [info, setInfo] = useState({
    open: false,
    type: "info",
    title: "",
    message: "",
  });
  const openInfo = (type, title, message) =>
    setInfo({ open: true, type, title, message });
  const closeInfo = () => setInfo((p) => ({ ...p, open: false }));

  // fetch insights
  useEffect(() => {
    const fetchInsights = async () => {
      try {
        const { data } = await axios.get(
          `${BACKEND_URL}/learners/${userId}/insights`
        );
        if (data?.insights) {
          setLearnerInsights(data.insights);
        }
      } catch (e) {
        console.error(e);
        openInfo("error", "Yükleme Hatası", "Ders analizi alınamadı.");
      }
    };
    fetchInsights();
  }, [userId]);

  // fetch courses
  useEffect(() => {
    const run = async () => {
      if (!userId) return;
      try {
        const { data } = await axios.get(`${BACKEND_URL}/courses/${userId}`);
        const formatted = (data?.courses || []).map((c) => ({
          id: c.id,
          name: c.name,
          totalEnrollments: c.totalEnrollments,
          averageProgress: Number(c.averageProgress || 0),
          completedEnrollments: c.completedEnrollments || 0,
          inProgressEnrollments: c.inProgressEnrollments || 0,
          activeEnrollments: c.activeEnrollments || 0,
          status: c.status,
        }));
        setCourses(formatted);
        if (formatted.length) setSelectedCourseId(String(formatted[0].id));
      } catch (e) {
        console.error(e);
        openInfo("error", "Yükleme Hatası", "Kurs listesi alınamadı.");
      }
    };
    run();
  }, [userId]);

  // fetch lessons for selected course
  useEffect(() => {
    const run = async () => {
      if (!selectedCourseId) return;
      try {
        const { data } = await axios.get(
          `${BACKEND_URL}/courses/id/${selectedCourseId}`
        );
        setCourseLessons(Array.isArray(data?.lessons) ? data.lessons : []);
        setSelectedLessonIdx(data?.lessons?.length ? "0" : "");
      } catch (e) {
        console.error(e);
        openInfo("error", "Yükleme Hatası", "Ders listesi alınamadı.");
        setCourseLessons([]);
        setSelectedLessonIdx("");
      }
    };
    run();
  }, [selectedCourseId]);

  const selectedCourse = useMemo(
    () => courses.find((c) => String(c.id) === String(selectedCourseId)),
    [courses, selectedCourseId]
  );

  const avgProgress = selectedCourse?.averageProgress ?? 0;
  const totalLessons = courseLessons.length;
  const completedApprox = totalLessons
    ? Math.round((avgProgress / 100) * totalLessons)
    : 0;
  const subText = totalLessons ? `${completedApprox}/${totalLessons}` : "—";
  const learnersCount = selectedCourse?.totalEnrollments ?? 0;

  const activeCount = selectedCourse?.activeEnrollments ?? 0;
  const completedCount = selectedCourse?.completedEnrollments ?? 0;
  const inProgressCount = selectedCourse?.inProgressEnrollments ?? 0;


  const activeRate = learnersCount ? (activeCount / learnersCount) * 100 : 0;
  const completedRate = learnersCount ? (completedCount / learnersCount) * 100 : 0;
  const inProgressRate = learnersCount ? (inProgressCount / learnersCount) * 100 : 0;

  // RAPOR EXPORT FONKSİYONLARI
  const exportLearnerReport = () => {
    const rows = learnerInsights.map((ins) => ({
      Ad: ins.learner.name,
      Soyad: ins.learner.surname,
      TelefonNo: ins.learner.number,
      KatılımTarihi: new Date(ins.learner.joinedAt).toLocaleDateString("tr-TR"),
      ToplamKursSayısı: ins.statistics.totalCourses,
      TamamlananKurs: ins.statistics.completedCourses,
      DevamEdenKurs: ins.statistics.inProgressCourses,
      TamamlamanDersSayısı: ins.statistics.totalLessonsCompleted,
    }));
    exportToExcel(rows, "Kullanici_Raporu");
  };

  const exportCourseReport = () => {
    const rows = courses.map((c) => ({
      KursAdı: c.name,
      KayıtlıKatılımcıSayisi: c.totalEnrollments,
      OrtalamaİlerlemeYüzdesi: c.averageProgress + "%",
      KursDurumu: c.status,
    }));
    exportToExcel(rows, "Kurs_Raporu");
  };

  const exportLessonReport = () => {
    const rows = [];
    learnerInsights.forEach((ins) => {
      ins.recentActivity.lessonProgress.forEach((lesson) => {
        rows.push({
          Katılımcı: ins.learner.name + " " + ins.learner.surname,
          DersBaşlık: lesson.lessonTitle,
          TamamlanmaTarihi: lesson.completedAt
            ? new Date(lesson.completedAt).toLocaleDateString("tr-TR")
            : "-",
          QuizSkoru: lesson.quizScore ?? "-",
        });
      });
    });
    exportToExcel(rows, "Ders_Raporu");
  };

  const exportActivityReport = () => {
    const rows = [];
    learnerInsights.forEach((ins) => {
      ins.recentActivity.messageHistory.forEach((msg) => {
        rows.push({
          Katılımcı: ins.learner.name + " " + ins.learner.surname,
          Mesaj: msg.messageBody,
          Durum: msg.messageStatus,
          İşlemTarihi: msg.readAt
            ? new Date(msg.readAt).toLocaleString("tr-TR")
            : msg.deliveredAt
            ? new Date(msg.deliveredAt).toLocaleString("tr-TR")
            : "-",
          DoğruCevap: msg.quizCorrectOption,
        });
      });
    });
    exportToExcel(rows, "Aktivite_Raporu");
  };

  return (
    <SharedLayout pageTitle="RAPORLAR">
      <div className="bg-gray-50 p-8 rounded-2xl shadow-sm">
        <div className="max-w-[1400px] mx-auto">
          {/* ÜST FİLTRE: KURS */}
          <div className="mb-6 sm:mb-8">
            <Select
              value={selectedCourseId}
              onChange={(e) => setSelectedCourseId(e.target.value)}
            >
              {courses.length === 0 && <option value="">KURS YOK</option>}
              {courses
                .filter((c) => c.status !== "DRAFT")
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
            </Select>
          </div>

          {/* EXPORT BUTTONS */}
          <div className="flex flex-wrap gap-3 mb-8">
            <button
              onClick={exportLearnerReport}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Kullanıcı Raporu
            </button>
            <button
              onClick={exportCourseReport}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
            >
              Kurs Raporu
            </button>
            <button
              onClick={exportLessonReport}
              className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
            >
              Ders Raporu
            </button>
            <button
              onClick={exportActivityReport}
              className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700"
            >
              Aktivite Raporu
            </button>
          </div>

          {/* ÜST SIRA */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            <div className="col-span-1 bg-white rounded-xl shadow-sm p-5 sm:p-6 flex flex-col justify-between min-h-[160px]">
              <div>
                <h4 className="text-green-600 font-medium mb-2">
                  KATILIMCILAR
                </h4>
                <p className="text-3xl sm:text-4xl font-bold">
                  {learnersCount}
                </p>
              </div>
            </div>

            <div className="col-span-1 md:col-span-2 bg-white rounded-xl shadow-sm p-5 sm:p-6">
              <h4 className="text-green-600 font-medium mb-4 sm:mb-5">
                EĞİTİM ÖLÇÜMLERİ
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 place-items-center">
                <ProgressCircle
                  value={activeRate}
                  size={84}
                  stroke={7}
                  label="AKTİF"
                  sub={`${activeCount}/${learnersCount}`}
                />
                <ProgressCircle
                  value={completedRate}
                  size={84}
                  stroke={7}
                  label="TAMAMLANAN"
                  sub={`${completedCount}/${learnersCount}`}
                />
                <ProgressCircle
                  value={inProgressRate}
                  size={84}
                  stroke={7}
                  label="SÜREÇ"
                  sub={`${inProgressCount}/${learnersCount}`}
                />
              </div>
            </div>
          </div>

          {/* ALT FİLTRE: DERSLER */}
          <div className="mt-8 mb-6">
            <Select
              value={selectedLessonIdx}
              onChange={(e) => setSelectedLessonIdx(e.target.value)}
            >
              {courseLessons.length === 0 && <option value="">DERS YOK</option>}
              {courseLessons.map((l, idx) => (
                <option key={idx} value={idx}>
                  {l.title || `LESSON ${idx + 1}`}
                </option>
              ))}
            </Select>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3">
            <div className="col-span-1 bg-white rounded-xl shadow-sm p-5 sm:p-6">
              <h4 className="text-green-600 font-medium mb-4">
                KONU ÖLÇÜMLERİ
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 place-items-center">
                <ProgressCircle
                  value={0}
                  size={80}
                  stroke={6}
                  label="AKTİF"
                  sub="—"
                />
                <ProgressCircle
                  value={0}
                  size={80}
                  stroke={6}
                  label="TAMAMLANAN"
                  sub="—"
                />
                <ProgressCircle
                  value={0}
                  size={80}
                  stroke={6}
                  label="SÜREÇ"
                  sub="—"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <InfoModal
        open={info.open}
        type={info.type}
        title={info.title}
        message={info.message}
        onClose={closeInfo}
      />
    </SharedLayout>
  );
};

export default InsightsPage;
